import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
  IconButton,
  Tooltip,
  Skeleton,
  TextField,
  Alert,
} from '@mui/material';
import { Edit, Add, Delete, Close, Save } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { headerSaga } from 'app/pages/Header/saga';
import { reducer, sliceKey } from 'app/pages/Header/slice';
import ModalMAIQ from 'app/components/ModalMAIQ';
import AddIcon from '@mui/icons-material/Add';
import DataGridMAIQ from 'app/components/DataGridMAIQ';
import DataGridTypes from 'types/DataGridTypes';
import FooterImageDetailsModal from './FooterImageDetailsModal';
import FooterImageService from 'app/apiService/footerImage/footerImageApiService';
import ConfirmationModal from 'app/components/ConfirmationModal';
import SingleDatePickerMAIQ from 'app/components/SingleDatePickerMAIQ';

// Modal for Add/Edit Footer Image
const FooterImageModal = ({ open, onClose, initialData, onSave, isSubmitting }: any) => {
  const theme = useTheme();
  const [form, setForm] = useState({
    image_description: '',
    image_address: '',
    image_url: '',
    expiry_date: '',
  });
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (initialData) {
      setForm({
        image_description: initialData.image_description || '',
        image_address: initialData.image_address || '',
        image_url: initialData.image_url || '',
        expiry_date: initialData.expiry_date ? initialData.expiry_date.split('T')[0] : '',
      });
    } else {
      setForm({
        image_description: '',
        image_address: '',
        image_url: '',
        expiry_date: '',
      });
    }
    setError('');
  }, [initialData, open]);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!form.image_description?.trim()) {
      setError('Image description is required');
      return false;
    }
    if (!form.image_address?.trim()) {
      setError('Image address is required');
      return false;
    }
    if (!form.image_url?.trim()) {
      setError('Landing page URL is required');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSave(form);
  };

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                {initialData ? 'Edit Footer Image' : 'Add New Footer Image'}
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          component="form"
          id="footer-image-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Image Description *
            </Typography>
            <TextField
              fullWidth
              value={form.image_description}
              onChange={e => handleChange('image_description', e.target.value)}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter image description"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Image Address *
            </Typography>
            <TextField
              fullWidth
              value={form.image_address}
              onChange={e => handleChange('image_address', e.target.value)}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter image URL"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Landing Page URL *
            </Typography>
            <TextField
              fullWidth
              value={form.image_url}
              onChange={e => handleChange('image_url', e.target.value)}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter landing page URL"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Expiry Date
            </Typography>
            <SingleDatePickerMAIQ
              value={form.expiry_date}
              onChange={(date) => handleChange('expiry_date', date || '')}
              placeholder="Select expiry date"
              minDate={new Date()}
              disabled={isSubmitting}
            />
          </Box>
        </Box>

        {/* Footer action bar */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              color: 'text.secondary',
              borderColor: 'divider',
              py: 0.75
            }}
          >
            Cancel
          </Button>
          {isSubmitting ? (
            <Skeleton
              variant="rectangular"
              width={140}
              height={36}
              sx={{
                borderRadius: 1,
                bgcolor: 'rgba(15, 40, 76, 0.1)',
              }}
            />
          ) : (
            <Button
              variant="contained"
              type="submit"
              form="footer-image-form"
              onClick={(e) => {
                // If form submission doesn't happen through the form's onSubmit,
                // trigger it manually here as a fallback
                handleSubmit(e as any);
              }}
              startIcon={<Save />}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600, 
                bgcolor: theme.palette.secondary.main,
                color: 'white',
                py: 0.75,
                px: 3,
                '&:hover': {
                  bgcolor: '#0a1f3a',
                }
              }}
            >
              {initialData ? 'Update Image' : 'Create Image'}
            </Button>
          )}
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

const FooterImageTab = () => {
  const theme = useTheme();
  const selectedDealer = useSelector(selectDealerSelected) || {};
  const selectedDealerId = selectedDealer.dealer || '';

  // Check if dealer is selected - if not, show message
  if (!selectedDealerId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          color: 'text.secondary',
          fontSize: '1.5rem',
          fontWeight: 500,
        }}
      >
        <Typography variant="h4" gutterBottom component="div">
          Please select a dealer to view footer images
        </Typography>
      </Box>
    );
  }

  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] = useState<any>(null);
  const [gridDeleteConfirm, setGridDeleteConfirm] = useState(false);

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (row: any) => {
    setEditData(row);
    setModalOpen(true);
  };

  const handleViewDetails = (row: any) => {
    setSelectedImage(row);
    setDetailModalOpen(true);
  };

  const handleDelete = async (row: any) => {
    setSelectedRowToDelete(row);
    setGridDeleteConfirm(true);
  };

  const confirmGridDelete = async () => {
    if (!selectedRowToDelete) return;
    setIsDeleting(true);
    try {
      await FooterImageService.deleteFooterImage(selectedRowToDelete._id);
      setRefreshKey(prev => prev + 1);
      setGridDeleteConfirm(false);
      setSelectedRowToDelete(null);
    } catch (err) {
      console.error('Delete footer image error', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const saveImage = async (id: string | null, payload: any) => {
    try {
      if (id) {
        await FooterImageService.editFooterImage(id, payload);
      } else {
        await FooterImageService.addFooterImage(payload);
      }
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Save footer image error', err);
    }
  };

  const handleSave = async (data: any) => {
    setIsSubmitting(true);
    try {
      const payload = { ...data, dealership_id: selectedDealerId };
      await saveImage(editData?._id || null, payload);
      setModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCellClick = (cell: any, clickedElement?: string) => {
    if (cell.field === 'actions') {
      // Handle action buttons (edit, delete)
      if (clickedElement === 'delete') {
        handleDelete(cell.row);
      } else if (clickedElement === 'edit') {
        handleEdit(cell.row);
      }
    } else if (cell.field === 'image_description') {
      // Handle image description click - open detail modal
      handleViewDetails(cell.row);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" sx={{ color: '#0f284c', fontWeight: 600 }}>
              Footer Secondary Offer Images
            </Typography>
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              Manage your email footer images and secondary offers
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: '#0a1f3a',
                boxShadow: '0 4px 10px rgba(15, 40, 76, 0.2)',
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Add New
          </Button>
        </Box>
        <DataGridMAIQ
          type={DataGridTypes.FOOTER_IMAGE(selectedDealerId)}
          onCellClick={handleCellClick}
          key={refreshKey}
        />
      </Paper>
      <FooterImageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialData={editData}
        onSave={handleSave}
        isSubmitting={isSubmitting}
      />
      <FooterImageDetailsModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        data={selectedImage}
        onSave={async payload => {
          await saveImage(selectedImage?._id, { ...payload, dealership_id: selectedDealerId });
          setDetailModalOpen(false);
          setRefreshKey(prev => prev + 1);
        }}
        onDelete={async () => {
          try {
            await FooterImageService.deleteFooterImage(selectedImage._id);
            setDetailModalOpen(false);
            setRefreshKey(prev => prev + 1);
          } catch (err) {
            console.error('Delete footer image error', err);
          }
        }}
      />
      <ConfirmationModal
        open={gridDeleteConfirm}
        onClose={() => setGridDeleteConfirm(false)}
        onConfirm={confirmGridDelete}
        message="Are you sure you want to delete this footer image?"
        loading={isDeleting}
      />
    </Box>
  );
};

export default FooterImageTab;
